import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useHierarchyContext } from "../contexts/HierarchyContext";
import { Panel } from "../components/layout/Panel";
import { CompGrid, type Column } from "../components/grid/CompGrid";
import {
  listAlertsForService,
  listChannelsForService,
  updateAlertForService,
  setAlertChannelAssignments,
  deleteAlertForService,
  updateChannelForService,
  deleteChannelForService,
  type Alert as AlertType,
  type ServiceAlertChannel,
} from "../api/client";

export function PageServiceAlerts() {
  const { hierarchy } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const [alerts, setAlerts] = useState<(AlertType & { channel_ids?: string[] })[]>([]);
  const [channels, setChannels] = useState<ServiceAlertChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = () => {
    if (!serviceSlug) return;
    const abort = new AbortController();
    setLoading(true);
    setError(null);
    Promise.all([
      listAlertsForService(serviceSlug, abort.signal),
      listChannelsForService(serviceSlug, abort.signal),
    ])
      .then(([alertsRes, channelsRes]) => {
        if (abort.signal.aborted) return;
        setAlerts(alertsRes);
        setChannels(channelsRes);
      })
      .catch((err) => {
        if (abort.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
    return () => abort.abort();
  };

  useEffect(load, [serviceSlug]);

  const handleToggleAlert = async (alertId: string, enabled: boolean) => {
    if (!serviceSlug) return;
    try {
      setActionError(null);
      await updateAlertForService(serviceSlug, alertId, { enabled }, undefined);
      setAlerts((prev) =>
        prev.map((a) => (a.id === alertId ? { ...a, enabled } : a)),
      );
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to update alert");
    }
  };

  const handleAlertChannelsChange = async (alertId: string, channelIds: string[]) => {
    if (!serviceSlug) return;
    try {
      await setAlertChannelAssignments(serviceSlug, alertId, channelIds, undefined);
      setAlerts((prev) =>
        prev.map((a) => (a.id === alertId ? { ...a, channel_ids: channelIds } : a)),
      );
    } catch {
      // ignore
    }
  };

  const handleToggleChannel = async (channelId: string, enabled: boolean) => {
    if (!serviceSlug) return;
    try {
      setActionError(null);
      await updateChannelForService(serviceSlug, channelId, { enabled }, undefined);
      setChannels((prev) =>
        prev.map((c) => (c.id === channelId ? { ...c, enabled } : c)),
      );
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to update channel");
    }
  };

  const handleDeleteChannel = async (channelId: string) => {
    if (!serviceSlug) return;
    try {
      setLoading(true);
      await deleteChannelForService(serviceSlug, channelId, undefined);
      setChannels((prev) => prev.filter((c) => c.id !== channelId));
      setAlerts((prev) =>
        prev.map((a) => ({
          ...a,
          channel_ids: (a.channel_ids ?? []).filter((id) => id !== channelId),
        })),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete channel");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAlert = async (alertId: string) => {
    if (!serviceSlug) return;
    try {
      setLoading(true);
      await deleteAlertForService(serviceSlug, alertId, undefined);
      setAlerts((prev) => prev.filter((a) => a.id !== alertId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete alert");
    } finally {
      setLoading(false);
    }
  };

  const formatCondition = (a: AlertType): string => {
    if (a.source_type === "health") {
      const statuses = (a.condition_config?.statuses as string[]) ?? ["degraded", "unhealthy"];
      const dur = (a.condition_config?.duration_minutes as number) | 0;
      return `${statuses.join(", ")}${dur > 0 ? ` for ${dur}min` : ""}`;
    }
    if (a.source_type === "metric") {
      const op = (a.condition_config?.operator as string) ?? "gt";
      const val = (a.condition_config?.value as number) ?? 0;
      const metric = (a.source_config?.metric_name as string) ?? "?";
      const dur = (a.condition_config?.duration_minutes as number) | 0;
      return `${metric} ${op} ${val}${dur > 0 ? ` for ${dur}min` : ""}`;
    }
    return "—";
  };

  const channelMap = Object.fromEntries(channels.map((c) => [c.id, c]));

  const alertColumns: Column<AlertType & { channel_ids?: string[] }>[] = [
    {
      id: "name",
      label: "Name",
      sortable: true,
      sortValue: (a) => a.name,
      render: (a) => (
        <Typography variant="body2" fontWeight={500}>
          {a.name}
        </Typography>
      ),
    },
    {
      id: "source",
      label: "Source",
      sortable: true,
      sortValue: (a) => a.source_type,
      render: (a) => (
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          {a.source_type}
        </Typography>
      ),
    },
    {
      id: "condition",
      label: "Condition",
      sortable: false,
      render: (a) => (
        <Typography variant="body2" className="om-text-mono" sx={{ fontSize: "0.8rem" }}>
          {formatCondition(a)}
        </Typography>
      ),
    },
    {
      id: "channels",
      label: "Channels",
      sortable: false,
      render: (a) => (
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Channels</InputLabel>
          <Select
            multiple
            value={Array.isArray(a.channel_ids) ? a.channel_ids : []}
            label="Channels"
            displayEmpty
            onChange={(e) => {
              const v = e.target.value;
              handleAlertChannelsChange(a.id, typeof v === "string" ? v.split(",") : v);
            }}
            renderValue={(selected) =>
              (selected as string[])
                .map((id) => channelMap[id]?.name || channelMap[id]?.channel_type || id)
                .join(", ") || "None"
            }
            MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
          >
            {channels.map((c) => {
              const selected = Array.isArray(a.channel_ids) ? a.channel_ids : [];
              return (
                <MenuItem key={c.id} value={c.id}>
                  <ListItemIcon>
                    <Checkbox checked={selected.indexOf(c.id) > -1} />
                  </ListItemIcon>
                  <ListItemText
                    primary={c.name || c.channel_type}
                    primaryTypographyProps={{ variant: "body2", sx: { opacity: c.enabled ? 1 : 0.5 } }}
                  />
                </MenuItem>
              );
            })}
            {channels.length === 0 && (
              <MenuItem disabled>
                <Typography variant="body2" sx={{ opacity: 0.6 }}>
                  No channels — create one below
                </Typography>
              </MenuItem>
            )}
          </Select>
        </FormControl>
      ),
    },
    {
      id: "enabled",
      label: "Enabled",
      sortable: true,
      sortValue: (a) => (a.enabled ? 1 : 0),
      render: (a) => (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Switch
            size="small"
            checked={a.enabled}
            onChange={(_, checked) => handleToggleAlert(a.id, checked)}
          />
          <Typography variant="body2" sx={{ opacity: a.enabled ? 1 : 0.5 }}>
            {a.enabled ? "On" : "Off"}
          </Typography>
        </Box>
      ),
    },
    {
      id: "actions",
      label:
        serviceSlug && alerts.length > 0 ? (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              variant="contained"
              component={RouterLink}
              to={`/services/${encodeURIComponent(serviceSlug)}/alerts/new`}
            >
              Create
            </Button>
          </Box>
        ) : null,
      render: (a) => (
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button
            size="small"
            variant="text"
            component={RouterLink}
            to={`/services/${encodeURIComponent(serviceSlug!)}/alerts/${encodeURIComponent(a.id)}`}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="text"
            color="error"
            onClick={() => handleDeleteAlert(a.id)}
            disabled={loading}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  const channelColumns: Column<ServiceAlertChannel>[] = [
    {
      id: "name",
      label: "Name",
      sortable: true,
      sortValue: (c) => c.name || c.channel_type,
      render: (c) => (
        <Typography variant="body2" fontWeight={500}>
          {c.name || c.channel_type}
        </Typography>
      ),
    },
    {
      id: "type",
      label: "Type",
      sortable: true,
      sortValue: (c) => c.channel_type,
      render: (c) => (
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          {c.channel_type}
        </Typography>
      ),
    },
    {
      id: "enabled",
      label: "Enabled",
      sortable: true,
      sortValue: (c) => (c.enabled ? 1 : 0),
      render: (c) => (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Switch
            size="small"
            checked={c.enabled}
            onChange={(_, checked) => handleToggleChannel(c.id, checked)}
          />
          <Typography variant="body2" sx={{ opacity: c.enabled ? 1 : 0.5 }}>
            {c.enabled ? "On" : "Off"}
          </Typography>
        </Box>
      ),
    },
    {
      id: "actions",
      label:
        serviceSlug ? (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              variant="contained"
              component={RouterLink}
              to={`/services/${encodeURIComponent(serviceSlug)}/channels/new`}
            >
              Create
            </Button>
          </Box>
        ) : null,
      render: (c) => (
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button
            size="small"
            variant="text"
            component={RouterLink}
            to={`/services/${encodeURIComponent(serviceSlug!)}/channels/${encodeURIComponent(c.id)}`}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="text"
            color="error"
            onClick={() => handleDeleteChannel(c.id)}
            disabled={loading}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  if (!serviceSlug) {
    return (
      <Box>
        <Alert severity="warning">No Service in URL. Please navigate from a product's services list.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
      {actionError && (
        <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 1 }}>
          {actionError}
        </Alert>
      )}
      <Panel title="Alerts">
        <CompGrid
          items={alerts}
          loading={loading}
          error={error}
          emptyMessage="No alerts configured. Create one to get notified when health degrades or metrics exceed thresholds."
          columns={alertColumns}
          keyExtractor={(a) => a.id}
          isItemDimmed={(a) => !a.enabled}
        />
        {alerts.length === 0 && !loading && !error && (
          <Box sx={{ pt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              component={RouterLink}
              to={`/services/${encodeURIComponent(serviceSlug)}/alerts/new`}
            >
              Create Alert
            </Button>
          </Box>
        )}
      </Panel>

      <Panel title="Notification channels">
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
          Channels are defined at service level. Assign them to alerts using the dropdown above.
        </Typography>
        <CompGrid
          items={channels}
          loading={false}
          error={null}
          emptyMessage="No channels. Create one to receive notifications."
          columns={channelColumns}
          keyExtractor={(c) => c.id}
          isItemDimmed={(c) => !c.enabled}
        />
        {channels.length === 0 && !loading && (
          <Box sx={{ pt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              component={RouterLink}
              to={`/services/${encodeURIComponent(serviceSlug)}/channels/new`}
            >
              Create Channel
            </Button>
          </Box>
        )}
      </Panel>
    </Box>
  );
}
